/**
 * Gold/silver switch inside the quick-add drawer.
 *
 * On the product page the pills are plain links (the other colour is another
 * product with its own URL). Inside the quick-add drawer a full navigation
 * would close the drawer and leave the collection, so here the counterpart
 * product is fetched with the drawer's section id and swapped into the open
 * drawer instead, the same way quick-add.js loads a product.
 */
class ColorSwitch extends HTMLElement {
  /** @type {AbortController | undefined} */
  #abort;

  connectedCallback() {
    if (!this.closest("#quick-add-drawer-content")) return;

    this.#abort = new AbortController();
    this.addEventListener("click", this.#onClick, { signal: this.#abort.signal });
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#abort = undefined;
  }

  /** @param {Event} event */
  #onClick = async (event) => {
    const link = event.target instanceof Element ? event.target.closest("a.color-switch__pill") : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const drawerContent = document.getElementById("quick-add-drawer-content");
    const sectionId = document.getElementById("quick-add-drawer")?.dataset.sectionId;
    if (!drawerContent || !sectionId) return; // fall back to the normal link

    event.preventDefault();
    this.setAttribute("aria-busy", "true");
    link.classList.add("is-loading");

    try {
      const url = new URL(link.href, window.location.origin);
      url.searchParams.set("section_id", sectionId);

      const response = await fetch(url.toString());
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = new DOMParser().parseFromString(await response.text(), "text/html");
      const content = html.querySelector("[data-quick-add-content]");
      if (!content) throw new Error("No quick-add content in response");

      drawerContent.innerHTML = content.innerHTML;
      drawerContent.closest(".v-scrollable, dialog")?.scrollTo?.({ top: 0 });
    } catch (error) {
      // Anything unexpected: behave like the plain link.
      window.location.href = link.href;
    } finally {
      this.removeAttribute("aria-busy");
      link.classList.remove("is-loading");
    }
  };
}

if (!customElements.get("color-switch")) {
  customElements.define("color-switch", ColorSwitch);
}
